import { useId } from 'react';
import { Icon } from './Icon';

/**
 * Form controls. One height, one radius, one focus ring, one error treatment.
 *
 * An error message says what went wrong and what to do about it — never an
 * apology and never just "invalid".
 */

const CONTROL =
  'w-full rounded-lg border border-line bg-surface px-3 text-sm text-ink placeholder:text-ink-3 ' +
  'transition-colors focus:border-brand focus:outline-none focus:ring-3 focus:ring-[color:var(--ring)] ' +
  'disabled:opacity-50 disabled:bg-surface-2';

export function Field({ label, hint, error, required, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      {label && (
        <span className="mb-1.5 flex items-baseline gap-1 text-[13px] font-medium text-ink-2">
          {label}
          {required && <span className="text-critical">*</span>}
        </span>
      )}
      {children}
      {error ? (
        <span className="mt-1.5 flex items-start gap-1 text-[12.5px] text-critical-ink">
          <Icon name="critical" size="xs" className="mt-0.5" />
          {error}
        </span>
      ) : (
        hint && <span className="mt-1.5 block text-[12.5px] text-ink-3">{hint}</span>
      )}
    </label>
  );
}

export function Input({ invalid = false, className = '', size = 'md', ...rest }) {
  return (
    <input
      className={[
        CONTROL,
        size === 'sm' ? 'h-8.5' : 'h-10',
        invalid ? 'border-critical focus:border-critical' : '',
        className,
      ].join(' ')}
      {...rest}
    />
  );
}

export function Select({ options, className = '', size = 'md', placeholder, ...rest }) {
  return (
    <div className="relative">
      <select
        className={[
          CONTROL,
          'appearance-none pr-9',
          size === 'sm' ? 'h-8.5' : 'h-10',
          className,
        ].join(' ')}
        {...rest}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <Icon
        name="chevronDown"
        size="sm"
        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-3"
      />
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Search', className = '' }) {
  const id = useId();
  return (
    <div className={`relative ${className}`}>
      <Icon
        name="search"
        size="sm"
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3"
      />
      <input
        id={id}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={`${CONTROL} h-8.5 pl-8 text-[13px]`}
      />
    </div>
  );
}

export function Textarea({ className = '', rows = 3, ...rest }) {
  return <textarea rows={rows} className={`${CONTROL} py-2 ${className}`} {...rest} />;
}

export function Checkbox({ label, description, className = '', ...rest }) {
  return (
    <label className={`flex cursor-pointer items-start gap-2.5 ${className}`}>
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-line-strong text-brand accent-[color:var(--brand)]"
        {...rest}
      />
      <span>
        <span className="block text-[13.5px] text-ink">{label}</span>
        {description && <span className="block text-[12.5px] text-ink-3">{description}</span>}
      </span>
    </label>
  );
}
